import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Menu, Pin, PinOff, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableItem({ item, handleTogglePin, handleEdit, handleDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="shadow-lg hover:shadow-xl transition-all border-0 overflow-hidden" data-testid={`menu-item-card-${item.text}`}>
        <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 gap-3 sm:gap-0">
          <div className="flex items-center space-x-2 sm:space-x-4 min-w-0 flex-1">
            <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex-shrink-0">
              <GripVertical className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            </div>
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center relative flex-shrink-0">
              {item.is_pinned && (
                <div className="absolute -top-1 -right-1 bg-yellow-400 rounded-full p-1">
                  <Pin className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                </div>
              )}
              <Menu className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-base sm:text-lg truncate">{item.text}</h3>
              <a 
                href={item.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs sm:text-sm text-purple-600 hover:text-purple-700 break-all"
              >
                {item.link}
              </a>
              {item.is_webapp && (
                <span className="ml-2 sm:ml-3 inline-block px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded-full">Web App</span>
              )}
            </div>
          </div>
          <div className="flex space-x-1 sm:space-x-2 justify-end sm:justify-start flex-shrink-0">
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-8 w-8 sm:h-9 sm:w-9 hover:bg-yellow-100"
              onClick={() => handleTogglePin(item.id)}
              data-testid={`pin-menu-item-${item.text}`}
              title={item.is_pinned ? 'Sabitlemeyi kaldır' : 'Sabitle'}
            >
              {item.is_pinned ? <PinOff className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Pin className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
            </Button>
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-8 w-8 sm:h-9 sm:w-9 hover:bg-purple-100"
              onClick={() => handleEdit(item)}
              data-testid={`edit-menu-item-${item.text}`}
            >
              <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Button>
            <Button 
              size="icon" 
              variant="ghost" 
              className="h-8 w-8 sm:h-9 sm:w-9 hover:bg-red-100 hover:text-red-600"
              onClick={() => handleDelete(item.id)}
              data-testid={`delete-menu-item-${item.text}`}
            >
              <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function MenuItems({ selectedTeam }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    text: '',
    link: '',
    is_webapp: false
  });

  useEffect(() => {
    if (selectedTeam) {
      loadItems();
    }
  }, [selectedTeam]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/menu-items`);
      setItems(res.data);
    } catch (err) {
      toast.error('Menü butonları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axiosInstance.put(`/teams/${selectedTeam}/menu-items/${editingItem.id}`, formData);
        toast.success('Menü butonu güncellendi');
      } else {
        await axiosInstance.post(`/teams/${selectedTeam}/menu-items`, formData);
        toast.success('Menü butonu eklendi');
      }
      setDialogOpen(false);
      setFormData({ text: '', link: '', is_webapp: false });
      setEditingItem(null);
      loadItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      text: item.text,
      link: item.link,
      is_webapp: item.is_webapp
    });
    setDialogOpen(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Bu menü butonunu silmek istediğinizden emin misiniz?')) return;
    
    try {
      await axiosInstance.delete(`/teams/${selectedTeam}/menu-items/${itemId}`);
      toast.success('Menü butonu silindi');
      loadItems();
    } catch (err) {
      toast.error('Menü butonu silinemedi');
    }
  };

  const openNewDialog = () => {
    setEditingItem(null);
    setFormData({ text: '', link: '', is_webapp: false });
    setDialogOpen(true);
  };

  const handleTogglePin = async (itemId) => {
    try {
      const res = await axiosInstance.post(`/teams/${selectedTeam}/menu-items/${itemId}/toggle-pin`);
      toast.success(res.data.is_pinned ? 'Menü butonu sabitlendi' : 'Sabitleme kaldırıldı');
      loadItems();
    } catch (err) {
      toast.error('İşlem başarısız');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);

      // Send all items with their order
      const itemsToUpdate = newItems.map((item, index) => ({
        id: item.id,
        order: index,
        ...(item.is_pinned && { pin_order: newItems.filter(i => i.is_pinned).indexOf(item) })
      }));

      try {
        await axiosInstance.post(`/teams/${selectedTeam}/menu-items/reorder`, {
          items: itemsToUpdate
        });
        toast.success('Sıralama kaydedildi');
      } catch (err) {
        toast.error('Sıralama kaydedilemedi');
        loadItems();
      }
    }
  };

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <Card className="w-full max-w-md text-center overflow-hidden">
          <CardContent className="pt-8 pb-8 sm:pt-12 sm:pb-12">
            <Menu className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-purple-500" />
            <h2 className="text-xl sm:text-2xl font-bold mb-2">Mağaza Seçin</h2>
            <p className="text-sm sm:text-base text-gray-600">Menü butonlarını yönetmek için bir mağaza seçin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="menu-items-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Menü Butonları</h1>
          <p className="text-sm sm:text-base text-gray-600">Üst menü butonlarınızı yönetin</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={openNewDialog}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg w-full sm:w-auto"
              data-testid="create-menu-item-button"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Yeni Buton
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-md" data-testid="menu-item-dialog">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl">{editingItem ? 'Butonu Düzenle' : 'Yeni Buton Ekle'}</DialogTitle>
              <DialogDescription className="text-sm">Menü butonu bilgilerini girin</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="text" className="text-sm sm:text-base">Buton Metni *</Label>
                  <Input
                    id="text"
                    value={formData.text}
                    onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                    placeholder="🛏️ WHATSAPP SHOP"
                    required
                    data-testid="menu-item-text-input"
                    className="text-sm sm:text-base"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="link" className="text-sm sm:text-base">Link *</Label>
                  <Input
                    id="link"
                    value={formData.link}
                    onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                    placeholder="https://delfieestore.whatsapp-shop.com"
                    required
                    data-testid="menu-item-link-input"
                    className="text-sm sm:text-base"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_webapp"
                    checked={formData.is_webapp}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_webapp: checked })}
                    data-testid="menu-item-webapp-switch"
                  />
                  <Label htmlFor="is_webapp" className="cursor-pointer text-sm sm:text-base">Web App Olarak Aç</Label>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button type="submit" className="bg-gradient-to-r from-purple-500 to-pink-500 w-full sm:w-auto" data-testid="save-menu-item-button">
                  {editingItem ? 'Güncelle' : 'Ekle'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : items.length === 0 ? (
        <Card className="text-center py-12 overflow-hidden">
          <CardContent>
            <Menu className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Hiç menü butonu yok</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">Yeni bir menü butonu ekleyerek başlayın</p>
          </CardContent>
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map(i => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3 sm:space-y-4">
              {items.map(item => (
                <SortableItem
                  key={item.id}
                  item={item}
                  handleTogglePin={handleTogglePin}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}
